import { z } from 'zod';
import { FuncaoSchema } from './candidatura';
import type { Funcao } from '../enums';
import { EventoStatusSchema, LocalSchema } from './evento';

/**
 * Feed (discovery) schemas — GET /eventos/publicos + /eventos/publicos/:id.
 *
 * Filtros do feed:
 *   - dataDe/dataAte: ISO datetime window
 *   - funcoes: array de funções (intersect com cadastradas do prof)
 *   - todasFuncoes: bypass filtro por função do profissional
 *   - cidade: typeahead IBGE (string match server-side em Json local.cidade)
 *   - uf: 2-letter
 *   - busca: nome OR descricao contains insensitive
 *   - page/limit: pagination
 */

// Query strings come in as strings — coerce booleans/numbers for HTTP boundary.
export const FeedFilterSchema = z.object({
  dataDe: z.iso.datetime().optional(),
  dataAte: z.iso.datetime().optional(),
  funcoes: z
    .preprocess((val) => {
      if (typeof val === 'string') {
        return val
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      }
      if (Array.isArray(val)) {
        return val.flatMap((item) =>
          typeof item === 'string'
            ? item
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            : [item],
        );
      }
      return val;
    }, z.array(FuncaoSchema))
    .optional(),
  todasFuncoes: z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
    .transform((v) => v === true || v === 'true' || v === '1')
    .optional()
    .default(false),
  cidade: z.string().max(80).optional(),
  uf: z.string().regex(/^[A-Z]{2}$/).optional(),
  busca: z.string().max(80).optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(50).default(20),
});

// Feed vaga subset — includes computed `aprovados` + `vagasDisponiveis`.
const FeedVagaSchema = z.object({
  id: z.string(),
  funcao: FuncaoSchema,
  quantidade: z.number().int(),
  aprovados: z.number().int(),
  vagasDisponiveis: z.number().int(),
  valorCents: z.number().int(),
  /**
   * Fila de espera disponível em eventos ABERTO quando vagasDisponiveis = 0 e a
   * fila ainda não atingiu o teto (quantidade * 3). Nunca vaza o tamanho da fila (Pitfall #7).
   */
  filaDisponivel: z.boolean().optional(),
});

const FeedEmpresaSubsetSchema = z.object({
  id: z.string(),
  razaoSocial: z.string(),
  nomeFantasia: z.string().nullable(),
  /** Phase 11 (MEDIA-03): canonical public URL of empresa logo; null when unset. */
  logoUrl: z.string().url().nullable(),
});

export const FeedItemSchema = z.object({
  id: z.string(),
  nome: z.string(),
  descricao: z.string().nullable(),
  dataInicio: z.string(),
  dataFim: z.string(),
  // enderecoStatus is internal (server-owned) — not part of the public feed shape.
  local: LocalSchema.omit({ enderecoStatus: true }),
  tipoContratacao: z.enum(['ABERTO', 'SELECAO']),
  empresa: FeedEmpresaSubsetSchema,
  vagas: z.array(FeedVagaSchema),
  funcoesAplicaveis: z.array(FuncaoSchema),
  totalVagas: z.number().int(),
  totalAprovados: z.number().int(),
  /**
   * Status da candidatura do profissional autenticado no evento, quando existente.
   */
  minhaCandidatura: z
    .object({
      status: z.enum([
        'pendente',
        'aprovada',
        'rejeitada',
        'concluida',
        'cancelada',
        'cancelada_empresa',
      ]),
      filaPosicao: z.number().int().nullable().optional(),
    })
    .nullable()
    .optional(),
});

export const FeedResponseSchema = z.object({
  eventos: z.array(FeedItemSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  hasMore: z.boolean(),
});

const FeedCandidaturaExistenteSchema = z.object({
  id: z.string(),
  status: z.enum([
    'pendente',
    'aprovada',
    'rejeitada',
    'concluida',
    'cancelada',
    'cancelada_empresa',
  ]),
  funcaoAplicada: FuncaoSchema,
  vagaId: z.string(),
  criadaEm: z.string(),
  // Phase 10 v1.1 — fila position in ABERTO waitlist. Non-null only when
  // status=pendente AND evento.tipoContratacao=ABERTO. Never leaks total queue
  // size (Pitfall #7 / LGPD).
  filaPosicao: z.number().int().nullable(),
  /**
   * Quantidade de recandidaturas restantes nesta vaga (máx 2 por profissional).
   * Presente apenas quando status = 'cancelada'.
   */
  recandidaturasRestantes: z.number().int().min(0).max(2).optional(),
  motivoCancelamento: z.string().nullable().optional(),
});

export const FeedDetalheSchema = FeedItemSchema.extend({
  status: EventoStatusSchema.optional(),
  candidaturaExistente: FeedCandidaturaExistenteSchema.nullable(),
  conflitoAgenda: z
    .object({
      eventoId: z.string(),
      eventoNome: z.string(),
      dataInicio: z.string(),
    })
    .nullable()
    .optional(),
});

export const FeedLocaisResponseSchema = z.object({
  ufs: z.array(z.string()),
  cidadesPorUf: z.record(z.string(), z.array(z.string())),
});

// ---------- Inferred types ----------

export type FeedFilter = z.infer<typeof FeedFilterSchema>;
export type FeedItem = z.infer<typeof FeedItemSchema>;
export type FeedResponse = z.infer<typeof FeedResponseSchema>;
export type FeedDetalhe = z.infer<typeof FeedDetalheSchema>;
export type FeedLocaisResponse = z.infer<typeof FeedLocaisResponseSchema>;
export type FeedFuncao = Funcao;

